import { Switch, Route, Redirect } from "wouter";
import { useEffect, lazy, Suspense } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/components/ThemeProvider";
import { getGuestUserId } from "@/lib/guestNotifications";
import { listenForMessages } from "@/lib/notifications";
import { registerServiceWorker } from "@/lib/pwa";
import InstallPrompt from "@/components/InstallPrompt";
import { UserRole } from "@shared/schema";
import { GTMScript } from "@/components/GTMScript";
import { usePageTracking } from "@/hooks/usePageTracking";

// Eager load - Public pages (most visited)
import Landing from "@/pages/landing";
import Track from "@/pages/track";
import Book from "@/pages/book";
import Register from "@/pages/register";
import LoyaltyBenefits from "@/pages/loyalty-benefits";
import Login from "@/pages/login";

// Auth pages
const ForgotPassword = lazy(() => import("@/pages/forgot-password"));

// Lazy load - Less frequently accessed pages
const Privacy = lazy(() => import("@/pages/privacy"));
const CorporateQuote = lazy(() => import("@/pages/corporate-quote"));
const ServicesPage = lazy(() => import("@/pages/services"));
const ServiceDetail = lazy(() => import("@/pages/service-detail"));
const SubscriptionsPublic = lazy(() => import("@/pages/subscriptions"));
const Contact = lazy(() => import("@/pages/contact"));
const Gallery = lazy(() => import("@/pages/gallery"));
const Feedback = lazy(() => import("@/pages/feedback"));
const BlogListingPage = lazy(() => import("@/pages/blog"));
const BlogDetailPage = lazy(() => import("@/pages/blog-detail"));
const FAQPage = lazy(() => import("@/pages/faq"));
const NotFound = lazy(() => import("@/pages/not-found"));

// Lazy load - Dashboard pages (authenticated users only)
const CustomerDashboard = lazy(() => import("@/pages/customer-dashboard"));
const CustomerProfile = lazy(() => import("@/pages/customer-profile"));
const CustomerSettings = lazy(() => import("@/pages/customer-settings"));
const CustomerSubscriptionsPage = lazy(() => import("@/pages/customer/SubscriptionsPage"));
const SubscriptionDetailPage = lazy(() => import("@/pages/subscription-detail"));
const MyVehicles = lazy(() => import("@/pages/my-vehicles"));
const VehicleDetail = lazy(() => import("@/pages/vehicle-detail"));
const BulkBook = lazy(() => import("@/pages/bulk-book"));
const EmployeeDashboard = lazy(() => import("@/pages/employee-dashboard"));
const EmployeeSettings = lazy(() => import("@/pages/employee-settings"));
const CashierDashboard = lazy(() => import("@/pages/cashier-dashboard"));
const CashierSettings = lazy(() => import("@/pages/cashier-settings"));

// Lazy load - Admin pages (heavy components)
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminSettings = lazy(() => import("@/pages/admin-settings"));
const AdminDashboardLegacy = lazy(() => import("@/pages/admin-dashboard"));

// Operations Hub
const AdminCalendarPage = lazy(() => import("@/pages/admin/operations/CalendarPage"));
const AdminWashesPage = lazy(() => import("@/pages/admin/operations/WashesPage"));
const AdminAppointmentsPage = lazy(() => import("@/pages/admin/operations/AppointmentsPage"));
const AdminInventoryPage = lazy(() => import("@/pages/admin/operations/InventoryPage"));
const AdminExpensesPage = lazy(() => import("@/pages/admin/operations/ExpensesPage"));
const AdminFinancialAnalyticsPage = lazy(() => import("@/pages/admin/operations/FinancialAnalyticsPage"));

// Marketing Hub
const AdminSmsPage = lazy(() => import("@/pages/admin/marketing/SmsPage"));
const AdminEmailPage = lazy(() => import("@/pages/admin/marketing/EmailPage"));
const AdminBlogPage = lazy(() => import("@/pages/admin/marketing/BlogPage"));
const AdminWebBuilderPageNew = lazy(() => import("@/pages/admin/marketing/WebBuilderPage"));
const AdminPopupsPageNew = lazy(() => import("@/pages/admin/marketing/PopupsPage"));
const AdminCouponsPage = lazy(() => import("@/pages/admin/marketing/CouponsPage"));
const AdminLoyaltyPage = lazy(() => import("@/pages/admin/marketing/LoyaltyPage"));
const AdminReviewsPage = lazy(() => import("@/pages/admin/marketing/ReviewsPage"));

// Corporate Hub
const AdminCompaniesPage = lazy(() => import("@/pages/admin/corporate/CompaniesPage"));
const AdminAgreementsPage = lazy(() => import("@/pages/admin/corporate/AgreementsPage"));
const AdminQuotesPage = lazy(() => import("@/pages/admin/corporate/QuotesPage"));
const AdminCompanyDetailPage = lazy(() => import("@/pages/admin/corporate/CompanyDetailPage"));

// System Hub
const AdminCustomersPage = lazy(() => import("@/pages/admin/system/CustomersPage"));
const AdminUsersPage = lazy(() => import("@/pages/admin/system/UsersPage"));
const AdminEmployeesPage = lazy(() => import("@/pages/admin/system/EmployeesPage"));
const AdminServicesPage = lazy(() => import("@/pages/admin/system/ServicesPage"));
const AdminSubscriptionPackagesPage = lazy(() => import("@/pages/admin/system/SubscriptionPackagesPage"));
const AdminSubscriptionManagementPage = lazy(() => import("@/pages/admin-subscription-management"));
const AdminSubscriptionsPage = lazy(() => import("@/pages/admin/subscriptions/index"));
const AdminThemePage = lazy(() => import("@/pages/admin/system/ThemePage"));
const AdminNotificationsPage = lazy(() => import("@/pages/admin/system/NotificationsPage"));
const AdminSettingsPage = lazy(() => import("@/pages/admin/system/SettingsPage"));

// Corporate Dashboard pages
const CorporateSubscriptionsPage = lazy(() => import("@/pages/corporate/SubscriptionsPage"));

// Analytics
const AnalyticsOverview = lazy(() => import("@/pages/admin/analytics/AnalyticsOverview"));
const BusinessInsightsPage = lazy(() => import("@/pages/admin/BusinessInsightsPage"));

// Legacy admin pages (to be refactored)
const AdminWebBuilder = lazy(() => import("@/pages/admin-web-builder"));
const AdminPopupsPage = lazy(() => import("@/pages/admin-popups"));
const AdminCustomerDetail = lazy(() => import("@/pages/admin-customer-detail"));
const AdminCorporateAgreements = lazy(() => import("@/pages/admin-corporate-agreements"));
const AdminCorporateAgreementForm = lazy(() => import("@/pages/admin-corporate-agreement-form"));
const AdminCustomerManagement = lazy(() => import("@/pages/admin-customer-management"));
const SetupPage = lazy(() => import("@/pages/setup"));
const CorporateCompanyDetailPage = lazy(() => import("@/components/CorporateCompanyDetailPage"));

// Loading component with better visibility
const PageLoader = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-white dark:from-gray-900 dark:to-gray-800">
    <div className="flex flex-col items-center gap-6">
      <div className="relative">
        <div className="animate-spin rounded-full h-20 w-20 border-4 border-blue-200 dark:border-blue-900"></div>
        <div className="animate-spin rounded-full h-20 w-20 border-4 border-primary border-t-transparent absolute top-0 left-0"></div>
      </div>
      <div className="flex flex-col items-center gap-2">
        <h2 className="text-2xl font-bold text-primary">TemizFilo</h2>
        <p className="text-muted-foreground text-sm animate-pulse">Yükleniyor...</p>
      </div>
    </div>
  </div>
);

function ProtectedRoute({ component: Component, allowedRoles }: { component: React.ComponentType; allowedRoles: string[] }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent"></div>
          <p className="text-muted-foreground text-sm animate-pulse">Kimlik doğrulanıyor...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Redirect to="/" />;
  }

  if (!allowedRoles.includes(user.role)) {
    return <Redirect to="/" />;
  }

  return <Component />;
}

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        {/* Public routes */}
        <Route path="/" component={Landing} />
        <Route path="/track" component={Track} />
        <Route path="/book" component={Book} />
        <Route path="/register" component={Register} />
        <Route path="/loyalty-benefits" component={LoyaltyBenefits} />
        <Route path="/login" component={Login} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/corporate-quote" component={CorporateQuote} />
        <Route path="/services" component={ServicesPage} />
        <Route path="/services/:id" component={ServiceDetail} />
        <Route path="/abonelikler" component={SubscriptionsPublic} />
        <Route path="/subscriptions" component={SubscriptionsPublic} />
        <Route path="/contact" component={Contact} />
        <Route path="/gallery" component={Gallery} />
        <Route path="/feedback" component={Feedback} />
        <Route path="/faq" component={FAQPage} />
        <Route path="/blog" component={BlogListingPage} />
        <Route path="/blog/:slug" component={BlogDetailPage} />
        <Route path="/subscription/:id" component={SubscriptionDetailPage} />
      
      {/* Protected routes */}
      <Route path="/customer">
        {() => <ProtectedRoute component={CustomerDashboard} allowedRoles={[UserRole.CUSTOMER]} />}
      </Route>
      <Route path="/customer/profile">
        {() => <ProtectedRoute component={CustomerProfile} allowedRoles={[UserRole.CUSTOMER]} />}
      </Route>
      <Route path="/customer/settings">
        {() => <ProtectedRoute component={CustomerSettings} allowedRoles={[UserRole.CUSTOMER]} />}
      </Route>
      <Route path="/customer/subscriptions">
        {() => <ProtectedRoute component={CustomerSubscriptionsPage} allowedRoles={[UserRole.CUSTOMER]} />}
      </Route>
      <Route path="/my-vehicles">
        {() => <ProtectedRoute component={MyVehicles} allowedRoles={[UserRole.CUSTOMER]} />}
      </Route>
      <Route path="/vehicles/:id">
        {() => <ProtectedRoute component={VehicleDetail} allowedRoles={[UserRole.CUSTOMER]} />}
      </Route>
      <Route path="/bulk-book">
        {() => <ProtectedRoute component={BulkBook} allowedRoles={[UserRole.CUSTOMER]} />}
      </Route>
      <Route path="/employee">
        {() => <ProtectedRoute component={EmployeeDashboard} allowedRoles={[UserRole.EMPLOYEE]} />}
      </Route>
      <Route path="/employee/settings">
        {() => <ProtectedRoute component={EmployeeSettings} allowedRoles={[UserRole.EMPLOYEE]} />}
      </Route>
      <Route path="/cashier">
        {() => <ProtectedRoute component={CashierDashboard} allowedRoles={[UserRole.CASHIER]} />}
      </Route>
      <Route path="/cashier/settings">
        {() => <ProtectedRoute component={CashierSettings} allowedRoles={[UserRole.CASHIER]} />}
      </Route>
      {/* Admin - New Sidebar Structure */}
      <Route path="/admin">
        {() => <ProtectedRoute component={AdminDashboard} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/analytics">
        {() => <ProtectedRoute component={AnalyticsOverview} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/business-insights">
        {() => <ProtectedRoute component={BusinessInsightsPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/settings">
        {() => <ProtectedRoute component={AdminSettings} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      
      {/* Operations Hub */}
      <Route path="/admin/operations/calendar">
        {() => <ProtectedRoute component={AdminCalendarPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/operations/washes">
        {() => <ProtectedRoute component={AdminWashesPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/operations/appointments">
        {() => <ProtectedRoute component={AdminAppointmentsPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/operations/inventory">
        {() => <ProtectedRoute component={AdminInventoryPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/operations/expenses">
        {() => <ProtectedRoute component={AdminExpensesPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/operations/financial">
        {() => <ProtectedRoute component={AdminFinancialAnalyticsPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      
      {/* Marketing Hub */}
      <Route path="/admin/marketing/sms">
        {() => <ProtectedRoute component={AdminSmsPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/marketing/email">
        {() => <ProtectedRoute component={AdminEmailPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/marketing/blog">
        {() => <ProtectedRoute component={AdminBlogPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/marketing/coupons">
        {() => <ProtectedRoute component={AdminCouponsPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/marketing/loyalty">
        {() => <ProtectedRoute component={AdminLoyaltyPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/marketing/reviews">
        {() => <ProtectedRoute component={AdminReviewsPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/admin-web-builder">
        {() => <ProtectedRoute component={AdminWebBuilder} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/marketing/popups">
        {() => <ProtectedRoute component={AdminPopupsPageNew} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      
      {/* Corporate Hub */}
      <Route path="/admin/corporate/companies">
        {() => <ProtectedRoute component={AdminCompaniesPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/corporate/companies/:id">
        {() => <ProtectedRoute component={AdminCompanyDetailPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/corporate/quotes">
        {() => <ProtectedRoute component={AdminQuotesPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/corporate/agreements">
        {() => <ProtectedRoute component={AdminAgreementsPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/corporate/agreement/:id">
        {() => <ProtectedRoute component={AdminCorporateAgreementForm} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      
      {/* System Hub */}
      <Route path="/admin/system/customers">
        {() => <ProtectedRoute component={AdminCustomersPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/system/users">
        {() => <ProtectedRoute component={AdminUsersPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/system/employees">
        {() => <ProtectedRoute component={AdminEmployeesPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/system/services">
        {() => <ProtectedRoute component={AdminServicesPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/system/theme">
        {() => <ProtectedRoute component={AdminThemePage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/system/notifications">
        {() => <ProtectedRoute component={AdminNotificationsPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/system/settings">
        {() => <ProtectedRoute component={AdminSettingsPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/system/subscription-packages">
        {() => <ProtectedRoute component={AdminSubscriptionPackagesPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/subscriptions">
        {() => <ProtectedRoute component={AdminSubscriptionsPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/subscription-management">
        {() => <ProtectedRoute component={AdminSubscriptionManagementPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/corporate/subscriptions">
        {() => <ProtectedRoute component={CorporateSubscriptionsPage} allowedRoles={[UserRole.CUSTOMER]} />}
      </Route>
      
      {/* Legacy routes (backward compatibility) */}
      <Route path="/admin-dashboard">
        {() => <ProtectedRoute component={AdminDashboardLegacy} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/setup">
        {() => <ProtectedRoute component={SetupPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/company/:companyId">
        {() => <ProtectedRoute component={CorporateCompanyDetailPage} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/customer/:id">
        {() => <ProtectedRoute component={AdminCustomerDetail} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      <Route path="/admin/customer-management">
        {() => <ProtectedRoute component={AdminCustomerManagement} allowedRoles={[UserRole.ADMIN]} />}
      </Route>
      
      {/* 404 */}
      <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function AppContent() {
  const { loading } = useAuth();
  
  // Show loader while auth is initializing
  if (loading) {
    return <PageLoader />;
  }
  
  return <Router />;
}

function App() {
  // Google Analytics Page Tracking
  usePageTracking();

  // Setup PWA Service Worker
  useEffect(() => {
    registerServiceWorker();
  }, []);

  // Setup guest ID only (notification permission will be requested on user interaction)
  useEffect(() => {
    const setupGuestUser = () => {
      // Check if user is logged in by checking localStorage
      const authUser = localStorage.getItem('firebase:authUser:' + import.meta.env.VITE_FIREBASE_API_KEY + ':[DEFAULT]');
      
      if (!authUser) {
        // Not logged in - just generate guest ID, don't request notification permission yet
        const guestId = getGuestUserId();
        
        // Listen for messages (permission will be requested later)
        listenForMessages((payload) => {
        });
      }
    };
    
    setupGuestUser();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <TooltipProvider>
            <GTMScript />
            <Toaster />
            <InstallPrompt />
            <AppContent />
          </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
